/*
  Ключи обмена с 1С — §3.6, карта `docs/arch/connector.md`.

  До 18.09.2026 сервер умел выдавать, перечислять и отзывать ключи, а нажать
  было негде: обмен с базой заказчика оставался недостижимым из кабинета - тот
  же дефект, что был у ссылки в кабинет должника до 07.09.2026.

  Три вещи, ради которых блок устроен именно так:

  1. **Значение показывается один раз.** В базе лежит только хеш, и восстановить
     ключ нельзя ни нам, ни вам. Поэтому оно остаётся на экране до перезагрузки,
     рядом стоит кнопка «Скопировать», а адрес обмена показан тут же: специалисту
     по 1С нужны оба значения сразу, и ходить за адресом в другое место незачем.
  2. **Отзыв спрашивают до, а не после.** Ключ живёт в настройках обработки на
     стороне заказчика; отозвать его значит остановить ночную выгрузку, и узнают
     об этом не по тревоге, а по тому, что данные перестали приходить.
  3. **Отметка об использовании стоит в перечне рядом с ключом.** Обмен без
     человека молчит одинаково и когда всё хорошо, и когда обработка в 1С
     перестала запускаться. Отличить одно от другого можно только по ней.
*/

import { useCallback, useState } from 'react'

import { api } from '../api/client.js'
import { useAccess } from './AccessContext.jsx'
import { Confirm, Field, Loading, Notice } from './Common.jsx'
import ConnectorKeyList from './ConnectorKeyList.jsx'
import usePagedList from './usePagedList.js'

/** Адрес, который вписывают в обработку 1С рядом с ключом. */
function exchangeAddress() {
  return window.location.origin
}

/*
  `issued` и `onIssued` приходят от страницы настроек (аудит 26.10.2026, причина 7):
  выданный ключ показывается ОДИН раз, а содержимое невыбранной вкладки не рисуется -
  ключ, лежавший в состоянии этой вкладки, пропадал при переключении на соседнюю, и
  увидеть его второй раз было нельзя.
*/
export default function SettingsConnectorKeys({ issued: heldIssued, onIssued: hold }) {
  // 21.10.2026: страницы перечня режет сервер (требование владельца). Выдача и отзыв
  // перечитывают текущую страницу через `reload`.
  const load = useCallback((window) => api.connectorKeys(window), [])
  const { rows: keys, error: listError, reload, bar } = usePagedList(load)
  const { readonly } = useAccess()
  const [error, setError] = useState(null)
  const [hint, setHint] = useState(null)
  // Хозяин передал держатель - ключ живёт у него; не передал (вкладка показана отдельно) -
  // здесь же, как и до правки.
  const [ownIssued, setOwnIssued] = useState(null)
  const held = hold !== undefined
  const issued = held ? heldIssued : ownIssued
  const setIssued = held ? hold : setOwnIssued
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  // Отложенный отзыв: какой ключ отзываем и чем он помечен.
  const [pending, setPending] = useState(null)
  const [pendingError, setPendingError] = useState(null)

  async function issue() {
    setBusy(true)
    setError(null)
    setHint(null)
    try {
      const key = await api.issueConnectorKey(note)
      setIssued(key)
      setNote('')
      reload()
      await copy(key.key)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  /*
    Ошибка отзыва остаётся ВНУТРИ окна: закрыть его и показать отказ за спиной
    человека значит сообщить, что действие прошло, когда оно не прошло (правило
    1 чек-листа формы).
  */
  async function revoke() {
    setBusy(true)
    setPendingError(null)
    try {
      await api.revokeConnectorKey(pending.key_id)
      setHint('Ключ отозван. Обработка 1С с этим ключом больше не принимается.')
      // Отозвали тот самый ключ, значение которого висит на экране, - убрать
      // его. Иначе над отозванным ключом остаётся надпись «запишите его
      // сейчас», и человек впишет в обработку значение, которое уже не примут
      // (найдено осмотром 18.09.2026).
      if (issued && issued.key_id === pending.key_id) setIssued(null)
      reload()
      setPending(null)
    } catch (failure) {
      setPendingError(failure)
    } finally {
      setBusy(false)
    }
  }

  async function copy(value) {
    try {
      await navigator.clipboard.writeText(value)
      setHint('Ключ скопирован - вставьте его в настройки обработки 1С.')
    } catch {
      // Браузер отказывает в буфере обмена без защищённого соединения. Молчать
      // нельзя: человек решит, что скопировалось, и передаст специалисту пустоту.
      setHint('Скопировать не удалось - выделите ключ в поле и скопируйте вручную.')
    }
  }

  if (listError && !keys) return <Notice error={listError} />
  if (!keys) return <Loading what="Читаем ключи обмена" />

  return (
    <>
      <h3 className="section__title">Обмен с вашей 1С</h3>
      <p className="muted">
        Обработка в вашей базе 1С присылает сведения о должниках сама, без человека, и предъявляет
        при этом ключ. Ключ выдаёте вы, живёт он в настройках обработки, и у каждой базы он свой:
        ключ одной базы не открывает портфель другой.
      </p>

      <ConnectorKeyList keys={keys} bar={bar} onRevoke={setPending} busy={busy || readonly} />

      <h3 className="section__title">Выдать ключ</h3>
      {/*
        Пояснение стоит ОТДЕЛЬНОЙ строкой под строкой с кнопкой, а не внутри
        поля: внутри оно удлиняет поле снизу, и кнопка уезжает под него на
        двадцать восемь пикселей (правило 6г скилла кабинета, замерено
        18.09.2026).
      */}
      <div className="row">
        <Field label="Пометка" width="m">
          <input
            className="field__input"
            value={note}
            maxLength={120}
            placeholder="Основная база 1С"
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        <button className="button" type="button" onClick={issue} disabled={busy || readonly}>
          Выдать ключ
        </button>
      </div>
      <p className="field__hint">
        Пометка нужна, чтобы потом узнать ключ в перечне: баз 1С у вас может быть не одна.
      </p>

      {error ? <Notice error={error} /> : null}
      {hint ? <Notice kind="ok">{hint}</Notice> : null}

      {issued ? (
        <>
          <Notice kind="warn">
            Ключ показывается один раз. Запишите его сейчас или передайте специалисту по 1С: в
            Арбитре ЖКХ хранится только отпечаток, и увидеть значение второй раз нельзя. Потеряли -
            выдайте новый, а прежний отзовите.
          </Notice>
          <div className="row">
            {/* Ширина `l`: ключ длиной в тридцать с лишним знаков, и в поле
                для имени он обрывается на середине - человек, у которого не
                сработал буфер обмена, переписал бы половину значения. */}
            <Field label="Ключ обмена - показывается один раз" width="l">
              <input
                className="field__input"
                readOnly
                value={issued.key}
                onFocus={(event) => event.target.select()}
              />
            </Field>
            <button className="button button--quiet" type="button" onClick={() => copy(issued.key)}>
              Скопировать
            </button>
          </div>
          <div className="row">
            <Field
              label="Адрес обмена"
              width="m"
              hint="Оба значения вписываются в обработку 1С: сначала адрес, потом ключ."
            >
              <input
                className="field__input"
                readOnly
                value={exchangeAddress()}
                onFocus={(event) => event.target.select()}
              />
            </Field>
          </div>
        </>
      ) : null}

      {pending ? (
        <Confirm
          title="Отозвать ключ обмена?"
          action="Отозвать"
          busy={busy}
          error={pendingError}
          onConfirm={revoke}
          onCancel={() => {
            setPending(null)
            setPendingError(null)
          }}
        >
          Ключ «{pending.note || 'без пометки'}» перестанет приниматься сразу. Если он вписан в
          работающую обработку 1С, ночная выгрузка остановится молча - данные просто перестанут
          приходить. Чтобы обмен продолжился, выдайте новый ключ и впишите его в обработку.
        </Confirm>
      ) : null}
    </>
  )
}
